import { __ } from '../../functions/object.functions';
import { SwiperElementOptions } from './swiper.types';

/**
 * Deep-merges multiple Swiper option objects into a single configuration.
 *
 * Later sources win over earlier ones. Plain objects (e.g. `pagination`, `autoplay`, `breakpoints`)
 * are merged recursively so a per-instance override like `{ autoplay: { delay: 3000 } }` does not
 * wipe out `pauseOnMouseEnter` coming from a theme. The additive arrays `modules`, `injectStyles`
 * and `injectStylesUrls` are concatenated and deduplicated; every other array is replaced by the
 * later value.
 *
 * @param sources - The option objects to merge, from lowest to highest precedence.
 * @returns {SwiperElementOptions} A new, merged option object. The inputs are not mutated.
 */
export function mergeSwiperOptions(...sources: (SwiperElementOptions | null | undefined)[]): SwiperElementOptions {
    const result: SwiperElementOptions = {};

    for (const source of sources) {
        if (__.IsNullOrUndefined(source)) {
            continue;
        }

        mergeInto(result as Record<string, unknown>, source as Record<string, unknown>);
    }

    return result;
}

/**
 * Recursively merges the given source object into the target object.
 *
 * @param target - The object being built up (mutated in place).
 * @param source - The object whose values take precedence.
 * @returns {void}
 */
function mergeInto(target: Record<string, unknown>, source: Record<string, unknown>): void {
    for (const key of Object.keys(source)) {
        const value = source[key];

        if (__.IsNullOrUndefined(value)) {
            continue;
        }

        if (key === 'modules' || key === 'injectStyles' || key === 'injectStylesUrls') {
            const previous = Array.isArray(target[key]) ? (target[key] as unknown[]) : [];
            target[key] = __.Distinct([...previous, ...(value as unknown[])], (item) => item);

            continue;
        }

        if (__.IsObject(value) && !Array.isArray(value) && !__.IsFunction(value)) {
            if (!__.IsObject(target[key]) || Array.isArray(target[key])) {
                target[key] = {};
            }

            mergeInto(target[key] as Record<string, unknown>, value as Record<string, unknown>);

            continue;
        }

        target[key] = value;
    }
}
